import { Dispatch, Reducer } from "react";
import AssetManager from "../../common/AssetManager";
import createDataContext from "../createDataContext";

import {
  Combination,
  CombinationException,
  CombinationSuggestion,
  Suggestable,
  SuggestionActions,
  SuggestionResult,
  SuggestionState,
} from "./types";

const suggestionReducer: Reducer<SuggestionState, SuggestionActions> = (
  state: SuggestionState,
  action: SuggestionActions
) => {
  switch (action.type) {
    case SuggestionResult.FOUND: {
      const suggestions = action.payload.suggestions;
      return {
        ...state,
        result: action.type,
        suggestions,
      };
    }
    case SuggestionResult.EXCEPTION_FOUND: {
      const exception = action.payload.exception;
      return {
        ...state,
        result: action.type,
        exception: exception,
      };
    }
    case SuggestionResult.IN_PROGRESS: {
      return {
        ...state,
        result: action.type,
        timeoutRef: action.payload.timeoutRef,
      };
    }
    case SuggestionResult.RESTORE: {
      return {
        ...state,
        result: action.type,
      };
    }
    case SuggestionResult.NOT_FOUND: {
      return {
        ...state,
        result: action.type,
      };
    }
    default:
      return new SuggestionState(SuggestionResult.IN_PROGRESS, -1, -1, null);
  }
};

const getClothesSuggestions =
  (dispatch: Dispatch<SuggestionActions>, state: SuggestionState) =>
  (
    location: number,
    temp: number[],
    humidity: number,
    ageGroup: number,
    sweatComment: number
  ) => {
    const combinations: Combination[] = AssetManager.getAsset(
      "/clothing_combinations_db.json"
    )["combinations"];
    const combinationExceptions: CombinationException[] = AssetManager.getAsset(
      "/clothing_exceptions_db.json"
    )["exceptions"];

    let currentExceptions: CombinationException[] = [];

    for (let currentTemp = temp[0]; currentTemp <= temp[1]; currentTemp++) {
      currentExceptions = [
        ...currentExceptions,
        ...(getAvailableSuggestables(
          combinationExceptions,
          location,
          currentTemp,
          humidity,
          ageGroup,
          sweatComment
        ) as CombinationException[]),
      ];
    }

    if (currentExceptions.length > 0) {
      clearTimeout(state.timeoutRef);
      dispatch({
        type: SuggestionResult.EXCEPTION_FOUND,
        payload: {
          exception: currentExceptions[0],
        },
      });
      return;
    }

    let suggestedCombinationsList: Combination[] = [];

    for (let currentTemp = temp[0]; currentTemp <= temp[1]; currentTemp++) {
      suggestedCombinationsList = [
        ...suggestedCombinationsList,
        ...(getAvailableSuggestables(
          combinations,
          location,
          currentTemp,
          humidity,
          ageGroup,
          sweatComment
        ) as Combination[]),
      ];
    }

    const suggestedCombinations: Combination[] = Array.from(
      new Set(suggestedCombinationsList.map((comb) => comb.combinationID))
    ).map(
      (id) =>
        suggestedCombinationsList.find((comb) => comb.combinationID === id)!
    );

    const suggestions = suggestedCombinations.map(
      (combination: Combination) => {
        return new CombinationSuggestion(
          combination.combinationID,
          combination.layers,
          combination.wikiID,
          combination.addToCartLink
        );
      }
    );

    if (suggestions.length > 0) {
      dispatch({
        type: SuggestionResult.FOUND,
        payload: {
          suggestions: suggestions,
        },
      });
    } else {
      dispatch({
        type: SuggestionResult.NOT_FOUND,
      });
    }
  };

export const getAvailableSuggestables = (
  suggestables: Suggestable[],
  location: number,
  temp: number,
  humidity: number,
  ageGroup: number,
  sweatComment: number
) => {
  const suggestablesAtLocation = suggestables.filter(
    (suggestable: Suggestable) => {
      return location === suggestable.location;
    }
  );
  // console.log("suggestablesAtLocation: ", suggestablesAtLocation);

  const suggestablesAtAgeGroup = suggestablesAtLocation.filter(
    (suggestable: Suggestable) => {
      return (
        ageGroup >= suggestable.ageGroup[0] &&
        ageGroup <= suggestable.ageGroup[1]
      );
    }
  );

  //console.log("suggestablesAtAgeGroup: ", suggestablesAtAgeGroup);

  const suggestablesAtSweatComment = suggestablesAtAgeGroup.filter(
    (suggestable: Suggestable) => {
      return (
        sweatComment >= suggestable.sweatComment[0] &&
        sweatComment <= suggestable.sweatComment[1]
      );
    }
  );

  // console.log("suggestablesAtSweatComment: ", suggestablesAtSweatComment);

  const suggestablesAtTemperature = suggestablesAtSweatComment.filter(
    (suggestable: Suggestable) => {
      return (
        temp >= suggestable.conditions.temperature[0] &&
        temp <= suggestable.conditions.temperature[1]
      );
    }
  );
  // console.log('suggestablesAtTemperature: ', suggestablesAtTemperature);

  const suggestablesAtHumidity = suggestablesAtTemperature.filter(
    (suggestable: Suggestable) => {
      return (
        humidity >= suggestable.conditions.humidity[0] &&
        humidity <= suggestable.conditions.humidity[1]
      );
    }
  );

  return suggestablesAtHumidity;
};

const restoreSuggestions =
  (dispatch: Dispatch<SuggestionActions>, state: SuggestionState) => () => {
    dispatch({
      type: SuggestionResult.RESTORE,
      payload: null,
    });
  };

export const { Context, Provider } = createDataContext(
  suggestionReducer,
  {
    getClothesSuggestions,
    restoreSuggestions,
  },
  new SuggestionState(SuggestionResult.IN_PROGRESS, -1, -1, null)
);
